/**
 * Cross-module bridge for capturing a 3D snapshot of the configurator scene
 * on demand.
 *
 * The producer side lives inside the R3F tree (MirrorScene.tsx) — only there
 * do we have direct access to `gl`, `scene` and the camera, which is what we
 * need to render a fresh frame from a *fixed* viewpoint (independent of
 * CursorSteerCamera's per-frame override) into an arbitrary 2D canvas.
 *
 * The consumer side lives outside the R3F tree (cpbSnapshotHijack.ts), which
 * monkey-patches `Konva.Stage.prototype.toCanvas` so that whenever CPB asks
 * its own preview stage for a snapshot, it gets *our* 3D pixels instead.
 * That's how the cart-line `_image` URL ends up pointing at our scene render
 * without us touching CPB's upload / property-attachment pipeline at all.
 *
 * Design notes:
 *  - Provider is a single global slot. There's only ever one R3F canvas in
 *    the page (`#viewer-3d-root canvas`), so a registry would be overkill.
 *  - The provider takes a *target canvas* rather than returning a data URL,
 *    because callers in the Konva path need an HTMLCanvasElement (Konva's
 *    `Stage.toCanvas()` contract). Returning a canvas avoids a roundtrip
 *    through PNG encode/decode.
 *  - If the provider isn't registered yet (e.g. snapshot requested before
 *    the scene mounted), the fallback paints a solid black rectangle — at
 *    worst CPB uploads a black PNG, which is harmless. Still better than
 *    throwing.
 */

/**
 * Fills `target` with the latest 3D scene render from a fixed front-view
 * camera, sized to `target.width` × `target.height`. Synchronous; the
 * producer is expected to do a one-shot `gl.render(...)` + `drawImage(...)`
 * before returning.
 */
export type SnapshotProviderFn = (target: HTMLCanvasElement) => void;

let provider: SnapshotProviderFn | null = null;

/**
 * Called from <SnapshotProvider /> inside the R3F tree once `gl`/`scene`
 * are available. Returns an unsubscribe — pass it to React's effect cleanup
 * so we don't keep a stale closure across HMR / unmount.
 */
export function registerSnapshotProvider(fn: SnapshotProviderFn): () => void {
  provider = fn;
  return () => {
    if (provider === fn) provider = null;
  };
}

/** Whether a 3D snapshot can currently be produced. */
export function hasSnapshotProvider(): boolean {
  return provider !== null;
}

/**
 * Synchronous fill. Always succeeds — falls back to a solid black rect if
 * the provider hasn't registered yet (race during page load). Callers in
 * the Konva hijack path can rely on the canvas being painted on return.
 */
export function fillCanvasWithSnapshot(target: HTMLCanvasElement): void {
  if (provider) {
    try {
      provider(target);
      return;
    } catch (err) {
      console.warn("[viewer] snapshot provider threw, using fallback:", err);
    }
  }
  const ctx = target.getContext("2d");
  if (ctx) {
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, target.width, target.height);
  }
}

/**
 * Convenience: build a fresh `<canvas>` of the requested size and fill it
 * with a 3D snapshot. Mirrors the shape Konva expects from
 * `Stage.toCanvas()` (an HTMLCanvasElement, not a wrapper).
 */
export function makeSnapshotCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  fillCanvasWithSnapshot(canvas);
  return canvas;
}
